/**
 * Funder interfaces — the agents that keep each health plan's billing rules
 * current (TRUS-778).
 *
 *   Source finder      searches the web for each plan's provider manual and
 *                      suggests it; a person accepts it as a source.
 *   Rules builder      reads each plan's source documents and drafts rules.
 *   Interface harness  checks the rules and sources, opens findings, and routes
 *                      each to a fix.
 *
 * All run on a schedule and on demand, for all plans or for plans picked in
 * Plans. None changes a source or a rule on its own: every suggested document
 * waits in Sources found, and every draft and correction waits in Review.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { funderAgentsApi, type FunderPlan, type FunderSummary } from '../../shared/services/funderAgentsApi';
import { GATE_COPY, type Notify } from '../components/funders/shared';
import PlansView, { type SourcesFocus } from '../components/funders/PlansView';
import ReviewView from '../components/funders/ReviewView';
import HarnessView, { type FixTarget } from '../components/funders/HarnessView';
import RulesView, { type RulesFocus } from '../components/funders/RulesView';
import RunsView from '../components/funders/RunsView';
import SuggestionsView from '../components/funders/SuggestionsView';

type View = 'plans' | 'found' | 'review' | 'harness' | 'rules' | 'runs';

export default function FunderInterfacesPanel({ canControl = true }: { canControl?: boolean }) {
  const [view, setView] = useState<View>('plans');
  const [summary, setSummary] = useState<FunderSummary | null>(null);
  const [plan, setPlan] = useState<FunderPlan | null>(null);
  const [sourcesFocus, setSourcesFocus] = useState<SourcesFocus | null>(null);
  const [rulesFocus, setRulesFocus] = useState<RulesFocus | null>(null);
  const [runsRefresh, setRunsRefresh] = useState(0);
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null);

  const notify: Notify = useCallback((ok, text) => setToast({ ok, text }), []);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), toast.ok ? 4000 : 8000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const loadSummary = useCallback(() => {
    funderAgentsApi
      .summary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, runsRefresh]);

  const onRunStarted = useCallback(() => setRunsRefresh((n) => n + 1), []);

  // A harness Fix that needs a person: open the plan's sources or its rules.
  const openFix = useCallback(async (t: FixTarget) => {
    if (t.view === 'sources') {
      setSourcesFocus({ payerId: t.payerId, payerName: t.payerName, sourceId: t.sourceId, message: t.message });
      setView('plans');
      return;
    }
    let found: FunderPlan | undefined;
    try {
      found = (await funderAgentsApi.plans({ q: t.payerName, limit: 20 })).plans.find((p) => p.payer_id === t.payerId);
    } catch {
      found = undefined;
    }
    // Rule counts are only for the header; the rules list loads by payer_id either way.
    setPlan(found ?? ({ payer_id: t.payerId, payer_name: t.payerName } as FunderPlan));
    setRulesFocus({ findingId: t.findingId, ruleIds: t.ruleIds, message: t.message });
    setView('rules');
  }, []);

  const open = summary?.findings_open;
  const openFindings = open ? open.high + open.medium + open.low : 0;
  const tabs: { value: View; label: string; count?: number }[] = [
    { value: 'plans', label: 'Plans' },
    { value: 'found', label: 'Sources found', count: summary?.suggestions_pending },
    { value: 'review', label: 'Review', count: summary?.proposals_pending },
    { value: 'harness', label: 'Harness', count: openFindings || undefined },
    { value: 'rules', label: 'Rules' },
    { value: 'runs', label: 'Runs' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ink">Funder interfaces</h3>
          <p className="text-sm text-ink-500">{GATE_COPY}</p>
        </div>
        {summary && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-paper-2 px-2 py-1 text-ink-700">{summary.plans_with_sources} plans with sources</span>
            <span className="rounded-full bg-paper-2 px-2 py-1 text-ink-700">{summary.sources_enabled} sources on</span>
            <span className="rounded-full bg-v2accent-soft px-2 py-1 text-v2accent-ink">{summary.proposals_pending} awaiting approval</span>
            {open && open.high > 0 && <span className="rounded-full bg-danger-soft px-2 py-1 text-danger-ink">{open.high} high findings</span>}
          </div>
        )}
      </div>

      {!canControl && (
        <p className="rounded-md bg-attention-soft px-3 py-2 text-sm text-attention-ink">
          You can look, but changes here need agent control rights; the server will refuse them.
        </p>
      )}

      <div role="tablist" aria-label="Funder interfaces" className="inline-flex flex-wrap gap-1 rounded-md bg-paper-2 p-1">
        {tabs.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={view === t.value}
            onClick={() => {
              setView(t.value);
              setSourcesFocus(null);
              setRulesFocus(null);
            }}
            className={`rounded px-3 py-1.5 text-sm font-medium ${view === t.value ? 'bg-surface text-ink shadow-v2-2' : 'text-ink-500 hover:text-ink'}`}
          >
            {t.label}
            {t.count ? <span className="ml-1.5 rounded-full bg-v2accent-soft px-1.5 text-xs text-v2accent-ink">{t.count}</span> : null}
          </button>
        ))}
      </div>

      {toast && (
        <p role="status" className={`rounded-md px-3 py-2 text-sm ${toast.ok ? 'bg-success-soft text-success-ink' : 'bg-danger-soft text-danger-ink'}`}>
          {toast.text}
        </p>
      )}

      <div className="min-w-0">
        {view === 'plans' && (
          <PlansView
            notify={notify}
            onRunStarted={onRunStarted}
            onOpenRules={(p) => {
              setPlan(p);
              setRulesFocus(null);
              setView('rules');
            }}
            focus={sourcesFocus}
          />
        )}
        {view === 'found' && <SuggestionsView notify={notify} onDecided={loadSummary} onRunStarted={onRunStarted} />}
        {view === 'review' && <ReviewView notify={notify} onDecided={loadSummary} />}
        {view === 'harness' && <HarnessView notify={notify} onRunStarted={onRunStarted} onOpen={(t) => void openFix(t)} />}
        {view === 'rules' && (
          <RulesView
            notify={notify}
            plan={plan}
            onPickPlan={(p) => {
              setPlan(p);
              setRulesFocus(null);
            }}
            focus={rulesFocus}
            onProposed={loadSummary}
          />
        )}
        {view === 'runs' && <RunsView notify={notify} refreshKey={runsRefresh} />}
      </div>
    </div>
  );
}

